import React, { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { Layout } from '../../components/Layout';
import { useAuth } from '../../contexts/AuthContext';
import { FirmAccount, normalizeFirm } from '../../types';

function tsToMs(ts: any): number {
  if (!ts) return 0;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts.seconds === 'number') return ts.seconds * 1000;
  return 0;
}

function fmtDate(ts: any): string {
  const ms = tsToMs(ts);
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

export function FirmSeats() {
  const { firmAdmin } = useAuth();
  const { firmId } = firmAdmin!;

  // Re-fetch the firm doc directly so seatsUsed reflects the latest value,
  // not the stale value cached at auth resolution time.
  const [firm, setFirm] = useState<FirmAccount | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => { load(); }, [firmId]);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const snap = await getDoc(doc(db, 'firms', firmId));
      if (!snap.exists()) throw new Error('Firm record not found.');
      setFirm(normalizeFirm(snap.id, snap.data()));
    } catch (e: any) {
      setError(e.message || 'Failed to load seat data.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading">Loading…</div>
      </Layout>
    );
  }

  if (error || !firm) {
    return (
      <Layout>
        <div style={{ maxWidth: 700 }}>
          <div className="error-message">{error || 'Firm data unavailable.'}</div>
        </div>
      </Layout>
    );
  }

  const termEndMs = tsToMs(firm.termEndDate);
  const termEnded = !firm.active || (termEndMs > 0 && termEndMs <= Date.now());
  const pct = firm.seatCount > 0 ? (firm.seatsUsed / firm.seatCount) * 100 : 0;
  const remaining = firm.seatCount - firm.seatsUsed;

  if (termEnded) {
    return (
      <Layout>
        <div style={{ maxWidth: 700 }}>
          <h1 className="page-title" style={{ marginBottom: 24 }}>Seat Usage</h1>
          <div className="card" style={{ textAlign: 'center', padding: 48 }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>—</div>
            <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8 }}>
              Term ended
            </div>
            <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24, lineHeight: 1.6 }}>
              This firm's outplacement term has concluded.
              {termEndMs > 0 && (
                <><br />Term ended {fmtDate(firm.termEndDate)}.</>
              )}
            </div>
            <div style={{ display: 'inline-grid', gridTemplateColumns: '1fr 1fr', gap: '0 48px', textAlign: 'left' }}>
              <Row label="Total Seats" value={String(firm.seatCount)} />
              <Row label="Seats Consumed" value={String(firm.seatsUsed)} />
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{ maxWidth: 700 }}>
        <div className="page-header">
          <h1 className="page-title">Seat Usage</h1>
          <button
            className="btn-secondary"
            onClick={load}
            style={{ padding: '6px 16px', fontSize: 13 }}
          >
            Refresh
          </button>
        </div>

        {/* Utilization bar */}
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 20 }}>
            Utilization
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0 32px', marginBottom: 24 }}>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Total Seats</div>
              <div style={{ fontSize: 32, fontWeight: 700 }}>{firm.seatCount}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Consumed</div>
              <div style={{ fontSize: 32, fontWeight: 700 }}>{firm.seatsUsed}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Remaining</div>
              <div style={{ fontSize: 32, fontWeight: 700, color: remaining === 0 ? 'var(--error)' : 'var(--accent-text)' }}>
                {remaining}
              </div>
            </div>
          </div>
          <div style={{ background: 'var(--bg-secondary)', borderRadius: 6, height: 12, overflow: 'hidden' }}>
            <div style={{
              height: '100%',
              width: `${Math.min(pct, 100)}%`,
              background: pct >= 90 ? 'var(--error)' : 'var(--accent)',
              borderRadius: 6,
              transition: 'width 0.4s ease',
            }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {Math.round(pct)}% consumed
            </span>
            {pct >= 90 && (
              <span style={{ fontSize: 12, color: 'var(--error)', fontWeight: 500 }}>
                Seats nearly full — contact Klarr to add seats
              </span>
            )}
          </div>
        </div>

        {/* Term dates */}
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16 }}>
            Term
          </div>
          <div style={{ display: 'grid', gap: 0 }}>
            <Row label="Term Start" value={fmtDate(firm.termStartDate)} />
            <Row label="Term End" value={fmtDate(firm.termEndDate)} />
            <Row label="Status" value={
              <span className="badge badge-active">Active</span>
            } />
          </div>
        </div>

        <div className="card">
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>
            Notes
          </div>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0, lineHeight: 1.6 }}>
            Seats are permanently consumed at enrollment and do not free up when a candidate is removed or unlinked.
            To add seats or extend the term, contact Klarr directly.
          </p>
        </div>
      </div>
    </Layout>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--border-light)' }}>
      <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{label}</span>
      <span style={{ fontSize: 13, color: 'var(--text-primary)' }}>{value}</span>
    </div>
  );
}
